"use client";

import { useRef, useState } from "react";
import Image from "next/image";
import {
  motion,
  AnimatePresence,
  useScroll,
  useMotionValueEvent,
  useTransform,
} from "framer-motion";

/* ─── Data ──────────────────────────────────────────────────────────────── */

const FORMATS = [
  {
    number: "01",
    badge: "Most Popular",
    title: "Stories",
    description:
      "Any genre, any age — fantasy, romance, adventure, mystery. AI writes it; audio reads it aloud, word by word.",
    tags: ["Kids", "Fantasy", "Adventure", "Romance"],
    excerpt:
      "In a forest where stars grew from the soil, Aria found a door no one else could see — and behind it, her name written in light.",
    stat: { value: "10+", label: "Genres available" },
    dotColor: "bg-blue-400",
    accentBar: "from-blue-400 to-indigo-400",
    image: "https://images.unsplash.com/photo-1532012197267-da84d127e765?w=1920&q=80",
    imageAlt: "Open books and magical storytelling",
  },
  {
    number: "02",
    badge: null,
    title: "Dramas",
    description:
      "Character-driven scenes told in dialogue. Tension, confessions, cliff-edge conversations — stories that feel overheard.",
    tags: ["Emotional arcs", "Real stakes", "Dialogue-first"],
    excerpt:
      "— You knew all along, didn't you?\n— Does it even matter now?",
    stat: { value: "100%", label: "Dialogue-driven" },
    dotColor: "bg-rose-400",
    accentBar: "from-rose-400 to-pink-400",
    image: "https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=1920&q=80",
    imageAlt: "Cinematic dramatic scene",
  },
  {
    number: "03",
    badge: null,
    title: "Anime",
    description:
      "Power arcs, rivals, tournaments. Serialised episodes with title cards that hit like an opening sequence.",
    tags: ["Power arcs", "Rivals", "Serialised"],
    excerpt:
      "Season 1 · Episode 03 — The Awakening. A challenger rises from the ruins of the old world. The arc begins now.",
    stat: { value: "EP.03", label: "The Awakening" },
    dotColor: "bg-violet-400",
    accentBar: "from-violet-400 to-purple-400",
    image: "https://images.unsplash.com/photo-1578632767115-351597cf2477?w=1920&q=80",
    imageAlt: "Vibrant anime aesthetic scene",
  },
  {
    number: "04",
    badge: null,
    title: "Netflix Seasons",
    description:
      "Write full serialised seasons — multi-episode arcs, recurring cast, cliffhangers per episode. Your world, your showrunner rules.",
    tags: ["Multi-episode", "Season arcs", "Cliffhangers", "Recurring cast"],
    excerpt:
      "Season 1 · 3 Episodes\nE01 · A New World\nE02 · The Hidden Path\nE03 · Shadows Rise ↯",
    stat: { value: "S01", label: "Your show, your rules" },
    dotColor: "bg-orange-400",
    accentBar: "from-orange-400 to-amber-400",
    image: "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=1920&q=80",
    imageAlt: "Cinematic theater atmosphere",
  },
  {
    number: "05",
    badge: null,
    title: "Poetry",
    description:
      "Verse, haiku, free form, spoken word — with narration that knows where to breathe.",
    tags: ["Verse", "Haiku", "Spoken word"],
    excerpt:
      "The night holds\nmore stars than\nwe have names for —\nand still, we reach.",
    stat: { value: "AI", label: "Voice narration" },
    dotColor: "bg-emerald-400",
    accentBar: "from-teal-400 to-emerald-400",
    image: "https://i.pinimg.com/736x/6a/e0/51/6ae05181535fd1ba722cfdd7bed3685c.jpg",
    imageAlt: "Vast starry night sky",
  },
];

/* ─── Component ─────────────────────────────────────────────────────────── */

export default function FeaturesSection() {
  const desktopRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const { scrollYProgress } = useScroll({
    target: desktopRef,
    offset: ["start start", "end end"],
  });

  const progressHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    const idx = Math.min(
      Math.floor(latest * FORMATS.length),
      FORMATS.length - 1
    );
    setActiveIndex(idx);
  });

  const fmt = FORMATS[activeIndex];

  return (
    <section id="features" className="relative bg-black">

      {/* ── DESKTOP: scroll-driven sticky ── */}
      <div
        ref={desktopRef}
        className="hidden md:block"
        style={{ height: `${(FORMATS.length + 1) * 180}vh` }}
      >
        <div className="sticky top-0 h-screen overflow-hidden">

          {/* Background images — crossfade */}
          {FORMATS.map((f, i) => (
            <motion.div
              key={f.number}
              className="absolute inset-0 z-0"
              animate={{ opacity: i === activeIndex ? 1 : 0 }}
              transition={{ duration: 2.2, ease: "easeInOut" }}
            >
              <Image
                src={f.image}
                alt={f.imageAlt}
                fill
                unoptimized
                className="object-cover object-center"
                priority={i === 0}
              />
            </motion.div>
          ))}

          {/* Cinematic overlays */}
          <div className="absolute inset-0 z-10 bg-gradient-to-r from-black/90 via-black/55 to-black/10" />
          <div className="absolute inset-0 z-10 bg-gradient-to-b from-black/50 via-transparent to-black/60" />

          {/* UI */}
          <div className="relative z-20 h-full flex flex-col">

            {/* Section header */}
            <div className="pt-20 pb-6 text-center shrink-0">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-8 h-px bg-white/25" />
                <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-white/40">
                  What You Can Build
                </span>
                <div className="w-8 h-px bg-white/25" />
              </div>
              <h2 className="leading-tight">
                <span className="block text-4xl font-black text-white sm:text-5xl md:text-6xl">
                  One platform.
                </span>
                <span className="block text-4xl font-black sm:text-5xl md:text-6xl bg-gradient-to-r from-yellow-400 to-yellow-200 bg-clip-text text-transparent">
                  Five ways to tell a story.
                </span>
              </h2>
            </div>

            {/* Two columns + center spine */}
            <div className="flex-1 flex items-center px-12 max-w-6xl mx-auto w-full pb-10 gap-0">

              {/* LEFT: editorial content */}
              <div className="flex-1 pr-12">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeIndex}
                    initial={{ opacity: 0, y: 32 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -24 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                  >
                    {/* Accent bar */}
                    <div className={`h-0.5 w-10 rounded-full bg-gradient-to-r ${fmt.accentBar} mb-6`} />

                    <span
                      className="block text-[7rem] font-black leading-none select-none text-white/10 mb-1"
                      aria-hidden="true"
                    >
                      {fmt.number}
                    </span>

                    {fmt.badge && (
                      <span className="mb-4 inline-block rounded-full border border-white/15 bg-white/10 backdrop-blur-sm px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white/70">
                        {fmt.badge}
                      </span>
                    )}

                    <h3 className="mb-4 text-5xl font-black text-white leading-tight">
                      {fmt.title}
                    </h3>

                    <p className="mb-7 text-base leading-relaxed text-white/60 max-w-xs">
                      {fmt.description}
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {fmt.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full border border-white/15 bg-white/8 backdrop-blur-sm px-3 py-1 text-xs font-medium text-white/70"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* CENTER: vertical spine */}
              <div className="relative flex flex-col items-center shrink-0 w-14">
                <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-white/12 rounded-full overflow-hidden">
                  <motion.div
                    className="w-full bg-gradient-to-b from-white/70 to-white/30 origin-top"
                    style={{ height: progressHeight }}
                  />
                </div>
                <div className="relative flex flex-col gap-8 py-2 z-10">
                  {FORMATS.map((f, i) => (
                    <div key={f.number} className="flex items-center justify-center w-8 h-8">
                      <motion.div
                        animate={{
                          scale: i === activeIndex ? 1.7 : 1,
                          opacity: i <= activeIndex ? 1 : 0.25,
                        }}
                        transition={{ duration: 0.35 }}
                        className={`w-2.5 h-2.5 rounded-full ${
                          i <= activeIndex ? f.dotColor : "bg-white/25"
                        }`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* RIGHT: glass excerpt card */}
              <div className="flex-1 pl-12">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeIndex}
                    initial={{ opacity: 0, x: 28 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-8 space-y-6"
                  >
                    {/* Excerpt / preview text */}
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/35 mb-3">
                        Preview
                      </p>
                      <p className="text-base italic leading-relaxed text-white/75 whitespace-pre-line">
                        &ldquo;{fmt.excerpt}&rdquo;
                      </p>
                    </div>

                    {/* Divider */}
                    <div className={`h-px w-full bg-gradient-to-r ${fmt.accentBar} opacity-30`} />

                    {/* Stat */}
                    <div>
                      <p className="text-3xl font-black text-white">{fmt.stat.value}</p>
                      <p className="text-xs font-medium text-white/40 mt-0.5">{fmt.stat.label}</p>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* Progress dots */}
            <div className="flex justify-center gap-2 pb-7 shrink-0">
              {FORMATS.map((_, i) => (
                <div
                  key={i}
                  className={`h-0.5 rounded-full transition-all duration-300 ${
                    i === activeIndex
                      ? "w-6 bg-white/70"
                      : i < activeIndex
                      ? "w-1.5 bg-white/35"
                      : "w-1.5 bg-white/15"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── MOBILE: full-image stacked ── */}
      <div className="md:hidden">
        <div className="py-16 px-6 text-center bg-gray-950">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-8 h-px bg-white/25" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-white/40">
              What You Can Build
            </span>
            <div className="w-8 h-px bg-white/25" />
          </div>
          <h2 className="leading-tight">
            <span className="block text-4xl font-black text-white">One platform.</span>
            <span className="block text-4xl font-black bg-gradient-to-r from-yellow-400 to-yellow-200 bg-clip-text text-transparent">
              Five ways to tell a story.
            </span>
          </h2>
        </div>

        {FORMATS.map((f) => (
          <motion.div
            key={f.number}
            className="relative overflow-hidden min-h-[80vh] flex flex-col justify-end"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <Image src={f.image} alt={f.imageAlt} fill unoptimized className="object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/55 to-black/15" />

            <div className="relative z-10 p-8 pb-10 space-y-5">
              <div className={`h-0.5 w-8 rounded-full bg-gradient-to-r ${f.accentBar}`} />
              <span className="block text-6xl font-black leading-none text-white/10 select-none">
                {f.number}
              </span>
              {f.badge && (
                <span className="inline-block rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white/70">
                  {f.badge}
                </span>
              )}
              <h3 className="text-4xl font-black text-white">{f.title}</h3>
              <p className="text-sm leading-relaxed text-white/60">{f.description}</p>
              <div className="flex flex-wrap gap-2">
                {f.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-white/15 bg-white/8 px-3 py-1 text-xs font-medium text-white/70"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              {/* Glass excerpt */}
              <div className="rounded-xl border border-white/10 bg-white/5 backdrop-blur-md p-5 mt-2">
                <p className="text-sm italic leading-relaxed text-white/65 whitespace-pre-line">
                  &ldquo;{f.excerpt}&rdquo;
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
